// src/pages/teacher/TeacherStudentsPage.jsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, TextField, InputAdornment,
  CircularProgress, Tooltip, Avatar, Dialog,
  DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import PeopleIcon    from '@mui/icons-material/People';
import RefreshIcon   from '@mui/icons-material/Refresh';
import SearchIcon    from '@mui/icons-material/Search';
import PersonIcon    from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FeedbackIcon  from '@mui/icons-material/Feedback';
import { useTranslation }   from 'react-i18next';
import { getMyStudents, getStudentDetails, sendFeedback } from '../../api/teacherApi';

function ProgressRing({ value, size = 36 }) {
  const r   = size / 2 - 4;
  const circ = 2 * Math.PI * r;
  const dash = circ * (value / 100);
  const color = value >= 70 ? '#16a34a' : value >= 50 ? '#d97706' : '#ef4444';
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke="#e2e8f0" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth={4}
          strokeDasharray={circ} strokeDashoffset={circ - dash}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset .5s' }} />
      </svg>
      <Typography sx={{ position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)', fontSize: 10, fontWeight: 700, color }}>
        {value}%
      </Typography>
    </Box>
  );
}

function StudentDetailDialog({ student, open, onClose, t }) {
  if (!student) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 700 }}>
            {(student.full_name || student.username).charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography fontWeight={700}>{student.full_name || student.username}</Typography>
            <Typography fontSize={12} color="text.secondary">{student.email}</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} mb={2}>
          {[
            { label: t('totalSessions'), value: student.total_sessions },
            { label: t('completed'),     value: student.completed },
            { label: t('passRate'),      value: student.pass_rate + '%' },
          ].map(s => (
            <Box key={s.label} sx={{ flex: 1, textAlign: 'center', p: 1.5,
              bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Typography fontSize={20} fontWeight={700}>{s.value}</Typography>
              <Typography fontSize={11} color="text.secondary">{s.label}</Typography>
            </Box>
          ))}
        </Stack>
        <Typography fontSize={13} fontWeight={600} mb={1}>{t('recentSessions')}</Typography>
        {(student.sessions || []).slice(0, 5).map((s, i) => (
          <Stack key={i} direction="row" justifyContent="space-between"
            alignItems="center" sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
            <Typography fontSize={12}>{s.lab_name}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {s.score != null && (
                <Typography fontSize={11} fontWeight={600}
                  color={s.passed ? '#16a34a' : '#ef4444'}>
                  {s.score.toFixed(1)}%
                </Typography>
              )}
              <Chip label={s.status} size="small"
                color={s.status === 'completed' ? 'success'
                  : s.status === 'running' ? 'warning' : 'default'} />
            </Stack>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TeacherStudentsPage() {
  const { t }          = useTranslation('teacher');
  const [students,     setStudents]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [detailStudent,setDetailStudent]= useState(null);
  const [detailOpen,   setDetailOpen]   = useState(false);
  const [loadingDetail,setLoadingDetail]= useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMyStudents({ search });
      setStudents(r.data?.students || []);
    } catch(e) {
      setError(e?.response?.data?.detail?.message || t('errorLoad'));
    } finally { setLoading(false); }
  }, [search, t]);

  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [load]);

  const handleViewDetails = async (userId) => {
    setLoadingDetail(true);
    setDetailOpen(true);
    try {
      const r = await getStudentDetails(userId);
      setDetailStudent(r.data);
    } catch { setDetailStudent(null); }
    finally { setLoadingDetail(false); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PeopleIcon sx={{ fontSize: 28, color: '#4f46e5' }} />
          <Typography variant="h5" fontWeight={700}>{t('students')}</Typography>
          <Chip label={students.length} size="small"
            sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 600 }} />
        </Stack>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Stack>

      <TextField size="small" placeholder={t('searchStudent')} value={search}
        onChange={e => setSearch(e.target.value)} sx={{ mb: 2, width: 320 }}
        InputProps={{ startAdornment: (
          <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
        )}} />

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell>{t('student')}</TableCell>
              <TableCell>{t('sessions')}</TableCell>
              <TableCell>{t('avgScore')}</TableCell>
              <TableCell>{t('passRate')}</TableCell>
              <TableCell>{t('exams')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('noStudents')}
                </TableCell>
              </TableRow>
            ) : students.map(s => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#eef2ff',
                      color: '#4f46e5', fontSize: 13, fontWeight: 700 }}>
                      {(s.full_name || s.username).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={13} fontWeight={600}>
                        {s.full_name || s.username}
                      </Typography>
                      <Typography fontSize={11} color="text.secondary">{s.email}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography fontSize={13}>{s.total_sessions}</Typography>
                  <Typography fontSize={11} color="text.secondary">
                    {s.completed} terminees
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ProgressRing value={Math.round(s.avg_score)} />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.pass_rate + '%'}
                    size="small"
                    color={s.pass_rate >= 70 ? 'success' : s.pass_rate >= 50 ? 'warning' : 'error'}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {s.passed_exams}/{s.total_exams}
                </TableCell>
                <TableCell>
                  <Tooltip title={t('viewDetails')}>
                    <IconButton size="small"
                      onClick={() => handleViewDetails(s.id)}>
                      <VisibilityIcon fontSize="small" sx={{ color: '#4f46e5' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <StudentDetailDialog
        student={detailStudent}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailStudent(null); }}
        t={t}
      />
    </Box>
  );
}
